
import { Component, namespace, Vue } from 'nuxt-property-decorator';
import {
  EventDto,
  PageDto,
  NestedPage,
  PageType,
  buildNestedPages,
  NavigationSectionColor,
  HeaderSectionLayout,
} from 'fourwaves-shared';
import {
  WebsiteNavigationList,
  WebsiteMobileNavigation,
  UserProfilePicture,
  UserDropdown,
  OrganizationSplashScreen,
  LangChooser,
} from 'fourwaves-shared/components';
import LiveBanner from './LiveBanner.vue';

const EventsModule = namespace('events');

@Component({
  components: {
    LiveBanner,
    WebsiteNavigationList,
    WebsiteMobileNavigation,
    LangChooser,
    UserProfilePicture,
    UserDropdown,
    OrganizationSplashScreen,
  },
})
export default class Navigation extends Vue {
  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.Getter isEventMultiLanguage!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.Getter isChair!: boolean;
  @EventsModule.State pages!: PageDto[];
  @EventsModule.Getter isLiveAppOpen!: boolean;

  showSplashScreen = false;
  isMobileNavigationOpen = false;
  isHeaderMounted = false;
  hasScrolled = false;

  get navigationClassNames(): Record<string, boolean> {
    return {
      '-dark': this.isDarkMode,
      '-transparent': this.isTransparent,
    };
  }

  get isDarkMode(): boolean {
    return this.currentEvent.navigationSection.colorTheme === NavigationSectionColor.Dark || this.isTransparent;
  }

  get isTransparent(): boolean {
    return (
      this.currentEvent.headerSection.layout === HeaderSectionLayout.FullWidth &&
      this.isHeaderMounted &&
      !this.hasScrolled &&
      !this.isMobileNavigationOpen
    );
  }

  get nestedPages(): NestedPage[] {
    const pages = this.pages
      .filter(page => page.isVisible)
      .filter(page => page.type !== PageType.Folder || this.pages.some(subPage => subPage.parentId === page.id));

    return buildNestedPages(pages);
  }

  get dashboardUrl(): string {
    return this.$api.getDashboardUrl(this.currentEvent.id);
  }

  public getRenderComponentKey(key: string): string {
    return `${this.isDarkMode}-${key}`;
  }

  public created() {
    this.$eventBus.on('header:mounted', this.onHeaderMounted);
    this.$eventBus.on('header:destroyed', this.onHeaderBeforeDestroy);
    this.$eventBus.on('header:visibility-progress-changed', this.onHeaderProgressChanged);
  }

  public beforeDestroy() {
    this.$eventBus.off('header:mounted', this.onHeaderMounted);
    this.$eventBus.off('header:destroyed', this.onHeaderBeforeDestroy);
    this.$eventBus.off('header:visibility-progress-changed', this.onHeaderProgressChanged);
  }

  public onHeaderMounted() {
    this.isHeaderMounted = true;
  }

  public onHeaderBeforeDestroy() {
    this.isHeaderMounted = false;
  }

  public onHeaderProgressChanged(progress: number) {
    this.hasScrolled = progress > 30;
  }

  public onSkipNavigationClick({ currentTarget }: MouseEvent) {
    if (!(currentTarget instanceof HTMLAnchorElement)) return;

    const anchorName = currentTarget.getAttribute('href')?.split('#')[1];
    const anchorElement = document.getElementById(anchorName || '');
    if (!anchorElement) return;

    anchorElement.focus();
    this.$scrollTo(anchorElement);
  }
}
